import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Users, ArrowRight, Copy, Check, Loader2, LogIn, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { createGroupSession, joinGroupSession } from "@/lib/groupSession";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";

const MOVIE_WORDS = [
  "Matrix", "Jedi", "Hobbit", "Groot", "Gandalf", "Neo", "Ripley", "Maverick",
  "Rocky", "Indy", "Marty", "Doc", "Yoda", "Spock", "Kirk", "Thor",
  "Loki", "Stark", "Fury", "Morpheus", "Trinity", "Joker", "Batman",
  "Pixel", "Reel", "Flick", "Scene", "Popcorn", "Cinema", "Montage",
  "Sequel", "Frame", "Script", "Dolby", "Retro", "Debut", "Epic",
  "Nova", "Cosmo", "Orbit", "Neon", "Prism", "Sonic", "Turbo",
];

const generateGuestName = () => {
  const word = MOVIE_WORDS[Math.floor(Math.random() * MOVIE_WORDS.length)];
  const num = Math.floor(100 + Math.random() * 900);
  return `${word}${num}`;
};

export interface GroupSessionData {
  session: any;
  member: any;
  isHost: boolean;
  hostIsPremium: boolean;
}

interface GroupLobbyProps {
  onSessionJoined: (data: GroupSessionData) => void;
  onBack: () => void;
  initialCode?: string;
}

const GroupLobby = ({ onSessionJoined, onBack, initialCode }: GroupLobbyProps) => {
  const { t } = useTranslation();
  const { user, username, refreshProfile } = useAuth();
  const [mode, setMode] = useState<"choose" | "create" | "join">(initialCode ? "join" : "choose");
  const [joinMode, setJoinMode] = useState<"pick" | "guest" | "signin">(user || initialCode ? "guest" : "pick");
  const [displayName, setDisplayName] = useState(username || user?.email?.split("@")[0] || generateGuestName());
  const [roomCode, setRoomCode] = useState(initialCode || "");
  const [loading, setLoading] = useState(false);

  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [signInLoading, setSignInLoading] = useState(false);
  const [signInError, setSignInError] = useState("");

  const [mediaType, setMediaType] = useState<"movie" | "tv">("movie");
  const [maxRuntime, setMaxRuntime] = useState(90);
  const [decisionTimer, setDecisionTimer] = useState(10);

  const runtimeLabel = maxRuntime >= 180 ? t("runtimeLabels.threeHoursPlus") : (maxRuntime % 60 > 0 ? t("runtimeLabels.hourMin", { h: Math.floor(maxRuntime / 60), m: maxRuntime % 60 }) : t("runtimeLabels.hourOnly", { h: Math.floor(maxRuntime / 60) }));

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError("");
    setSignInLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email: signInEmail, password: signInPassword });
      if (error) throw error;
      const { data: profile } = await (supabase.from("profiles") as any)
        .select("username")
        .eq("id", data.user.id)
        .single();
      const name = profile?.username || data.user.email?.split("@")[0] || "Player";
      setDisplayName(name);
      await refreshProfile();
      setJoinMode("guest");
      toast.success(t("misc.signedInAs", { name }));
    } catch (err: any) {
      setSignInError(err.message || t("common.error"));
    } finally {
      setSignInLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!displayName.trim()) { toast.error(t("group.enterName")); return; }
    setLoading(true);
    try {
      const result = await createGroupSession({
        mediaType,
        maxRuntime,
        decisionTimer,
        displayName: displayName.trim(),
        userId: user?.id,
      });
      onSessionJoined({ session: result.session, member: result.member, isHost: true, hostIsPremium: result.host_is_premium ?? false });
    } catch (e: any) {
      toast.error(e.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!displayName.trim()) { toast.error(t("group.enterName")); return; }
    if (roomCode.length !== 6) { toast.error(t("group.enterRoomCode")); return; }
    setLoading(true);
    try {
      const result = await joinGroupSession(roomCode, displayName.trim(), user?.id);
      onSessionJoined({ session: result.session, member: result.member, isHost: false, hostIsPremium: result.host_is_premium ?? false });
    } catch (e: any) {
      if (e.message?.includes("FREE_GROUP_LIMIT")) {
        toast.error(t("group.lobbyFull"), { duration: 6000 });
      } else {
        toast.error(e.message || t("common.error"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4 py-6 overflow-y-auto">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md bg-gradient-card border border-border rounded-2xl p-6 shadow-card space-y-6"
      >
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-primary/15 mx-auto mb-3 flex items-center justify-center">
            <Users className="w-7 h-7 text-primary" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">{t("group.title")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("group.subtitle")}</p>
        </div>

        <div>
          <label className="text-xs text-muted-foreground tracking-wider mb-2 block">{t("group.yourName")}</label>
          <div className="relative">
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t("group.enterName")}
              maxLength={20}
              className="w-full px-4 pr-12 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {!user && (
              <button
                type="button"
                onClick={() => setDisplayName(generateGuestName())}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-muted transition-colors"
                title={t("landing.generateRandom")}
              >
                <RefreshCw className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {mode === "choose" && (
            <motion.div key="choose" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
              <button
                onClick={() => setMode("create")}
                className="w-full py-3.5 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                {t("group.createRoom")}
              </button>
              <button
                onClick={() => setMode("join")}
                className="w-full py-3.5 rounded-xl bg-secondary text-foreground font-display font-semibold text-base border border-border active:scale-95 transition-transform flex items-center justify-center gap-2"
              >
                {t("group.joinRoom")}
              </button>
            </motion.div>
          )}

          {mode === "create" && (
            <motion.div key="create" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-5">
              <div>
                <label className="text-xs text-muted-foreground tracking-wider mb-3 block">{t("group.weWantToWatch")}</label>
                <div className="flex gap-3">
                  {([{ type: "movie" as const, emoji: "🎬", label: t("common.movie") }, { type: "tv" as const, emoji: "📺", label: t("common.tvShow") }]).map((opt) => (
                    <button
                      key={opt.type}
                      onClick={() => setMediaType(opt.type)}
                      className={`flex-1 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all active:scale-95 ${
                        mediaType === opt.type ? "border-primary bg-primary/10" : "border-border bg-secondary hover:border-muted-foreground/30"
                      }`}
                    >
                      <span className="text-2xl">{opt.emoji}</span>
                      <span className={`text-sm font-medium ${mediaType === opt.type ? "text-primary" : "text-muted-foreground"}`}>{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {mediaType === "movie" && (
                <div>
                  <label className="text-xs text-muted-foreground tracking-wider mb-1 block">{t("group.maxRuntime")}</label>
                  <p className="text-lg font-display font-bold text-foreground mb-3">{runtimeLabel}</p>
                  <Slider value={[maxRuntime]} onValueChange={([v]) => setMaxRuntime(v)} min={30} max={180} step={30} />
                </div>
              )}

              <div>
                <label className="text-xs text-muted-foreground tracking-wider mb-1 block">{t("group.decisionTimer")}</label>
                <p className="text-lg font-display font-bold text-foreground mb-3">{decisionTimer} min</p>
                <Slider value={[decisionTimer]} onValueChange={([v]) => setDecisionTimer(v)} min={5} max={30} step={5} />
              </div>

              <div className="flex gap-2">
                <button onClick={() => setMode("choose")} className="px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm font-medium active:scale-95 transition-transform">{t("common.back")}</button>
                <button
                  onClick={handleCreate}
                  disabled={loading}
                  className="flex-1 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> {t("common.create")}</>}
                </button>
              </div>
            </motion.div>
          )}

          {mode === "join" && (
            <motion.div key="join" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-5">
              {joinMode === "pick" && (
                <div className="space-y-3">
                  <button
                    onClick={() => setJoinMode("guest")}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 transition-all active:scale-[0.98]"
                  >
                    <span className="text-2xl">👤</span>
                    <div className="text-left">
                      <p className="font-display font-semibold text-foreground text-sm">{t("group.joinAsGuest")}</p>
                      <p className="text-xs text-muted-foreground">{t("group.guestDesc")}</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setJoinMode("signin")}
                    className="w-full flex items-center gap-3 p-4 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 transition-all active:scale-[0.98]"
                  >
                    <LogIn className="w-6 h-6 text-primary" />
                    <div className="text-left">
                      <p className="font-display font-semibold text-foreground text-sm">{t("group.signInJoin")}</p>
                      <p className="text-xs text-muted-foreground">{t("group.signInJoinDesc")}</p>
                    </div>
                  </button>
                  <button onClick={() => setMode("choose")} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors mt-2">
                    ← {t("common.back")}
                  </button>
                </div>
              )}

              {joinMode === "signin" && (
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div>
                    <label className="text-xs text-muted-foreground tracking-wider mb-1 block">{t("common.email")}</label>
                    <input
                      type="email"
                      value={signInEmail}
                      onChange={(e) => setSignInEmail(e.target.value)}
                      required
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground tracking-wider mb-1 block">{t("common.password")}</label>
                    <input
                      type="password"
                      value={signInPassword}
                      onChange={(e) => setSignInPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  {signInError && (
                    <p className="text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">{signInError}</p>
                  )}
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setJoinMode("pick")} className="px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm font-medium active:scale-95 transition-transform">{t("common.back")}</button>
                    <button
                      type="submit"
                      disabled={signInLoading}
                      className="flex-1 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {signInLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><LogIn className="w-4 h-4" /> {t("common.signIn")}</>}
                    </button>
                  </div>
                </form>
              )}

              {joinMode === "guest" && (
                <>
                  <div>
                    <label className="text-xs text-muted-foreground tracking-wider mb-2 block">{t("group.roomCode")}</label>
                    <input
                      type="text"
                      inputMode="text"
                      maxLength={6}
                      value={roomCode}
                      onChange={(e) => setRoomCode(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 6))}
                      placeholder="ABC123"
                      className="w-full px-4 py-3 rounded-xl bg-secondary border border-border text-foreground text-center text-2xl font-display font-bold tracking-[0.3em] focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button onClick={() => user ? setMode("choose") : setJoinMode("pick")} className="px-4 py-3 rounded-xl bg-secondary text-muted-foreground text-sm font-medium active:scale-95 transition-transform">{t("common.back")}</button>
                    <button
                      onClick={handleJoin}
                      disabled={loading || roomCode.length !== 6}
                      className="flex-1 py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-base shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ArrowRight className="w-4 h-4" /> {t("common.join")}</>}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <button onClick={onBack} className="w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors">
          {t("group.backToSolo")}
        </button>
      </motion.div>
    </div>
  );
};

export default GroupLobby;
